// AVR-03 (amplification) and AVR-04 (pre-outs) — README.md. Both read the
// already-computed `ctx.assignment` (assignment.ts) rather than re-deriving
// who moved out; this file only turns that structured result into Issues.

import type { ChannelGroup } from '@roomtreatment/av-catalog';
import type { BuildContext } from '../context';
import type { Issue } from '../types';
import { productName, issueId, countNoun, GROUP_LABEL } from '../copy';

/** Fixed priority order — must match assignment.ts's MOVE_ORDER so AVR-04's
 *  issue array order is deterministic under speaker reorder (Map iteration
 *  order over `noPreout` would otherwise follow first-seen order, which
 *  depends on `doc.speakers`). */
const GROUP_ORDER: ChannelGroup[] = [
  'height2', 'height1', 'front', 'center', 'surround_back', 'surround',
];

export function checkAvr03(ctx: BuildContext): Issue[] {
  if (!ctx.avUnit || !ctx.assignment) return [];
  const k = ctx.assignment.noAmpChannel.length;
  if (k === 0) return [];
  const subjects = ctx.assignment.noAmpChannel.map((a) => a.speakerId);
  return [{
    id: issueId('AVR-03', subjects),
    rule: 'AVR-03',
    severity: 'error',
    text: `${countNoun(k, 'speaker')} ${k === 1 ? 'has' : 'have'} no amplifier channel.`,
    fix: `Add a ${k}-channel amplifier.`,
    subjects,
  }];
}

export function checkAvr04(ctx: BuildContext): Issue[] {
  if (!ctx.avUnit || !ctx.assignment) return [];
  const issues: Issue[] = [];
  for (const group of GROUP_ORDER) {
    const members = ctx.assignment.noPreout.get(group);
    if (!members || members.length === 0) continue;
    const subjects = members.map((m) => m.speakerId);
    issues.push({
      id: issueId('AVR-04', subjects),
      rule: 'AVR-04',
      severity: 'error',
      text: `${productName(ctx.avUnit.product)} has no ${GROUP_LABEL[group]} pre-outs.`,
      fix: `Choose a receiver with ${GROUP_LABEL[group]} pre-outs, or remove those speakers.`,
      subjects,
    });
  }
  return issues;
}
